import AccommodationIndividualSelectRoomOptionTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTile'
import AccommodationIndividualSelectRoomOptionTileCTA from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileCTA'
import AccommodationIndividualSelectRoomOptionTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileDetails'
import AccommodationIndividualSelectRoomOptionTilePricing from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTilePricing'
import TextButton from 'LuxKit/Button/TextButton'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import PropertyRatePrice from './PropertyRatePrice'
import { isPropertyInclusionValidForDuration, getPropertyRatePassportOffer } from './singlePropertyUtils'

interface Props {
  propertyRoom: App.Property.Room
  rate?: App.EnrichedPropertyRate
  isSelected: boolean
  optionVariant: 'stacked' | 'carousel'
  duration: number
  property: App.Property.Property
  occupancyRoom: OfferPageRoom
  onSelect: (optionId: string | undefined) => void
}

function getHeadlineCancellationPolicyStart(rate: App.EnrichedPropertyRate | undefined): Date | undefined {
  if (!rate?.headlineCancellationPolicyStart) return undefined
  return new Date(rate.headlineCancellationPolicyStart)
}

function PropertyIndividualRoomSelectRoomOption(props: Props) {
  const {
    propertyRoom,
    rate,
    isSelected,
    optionVariant,
    duration,
    property,
    occupancyRoom,
    onSelect,
  } = props
  const intl = useIntl()
  const propertyTimezone = property.location.timezone
  const noRateMode = !rate
  const optionLabel = noRateMode ?
    undefined :
    rate.name
  const [roomInclusions, roomBonusInclusions, roomLuxPlusInclusions, roomBundleInclusions] = [
    propertyRoom.inclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
    propertyRoom.bonusInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
    propertyRoom.luxPlusInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
    propertyRoom.bundleInclusions.filter(inclusion => isPropertyInclusionValidForDuration(inclusion, duration)),
  ]
  const showNoRateInclusions = noRateMode && (
    roomInclusions.length > 0 ||
    roomBonusInclusions.length > 0 ||
    roomLuxPlusInclusions.length > 0 ||
    roomBundleInclusions.length > 0
  )
  const showRateInclusions = !!rate && (
    rate.luxPlusInclusions.length > 0 ||
    rate.bonusInclusions.length > 0 ||
    rate.bundleInclusions.length > 0 ||
    rate.inclusions.length > 0
  )
  const passportOffer = useMemo(
    () => rate ? getPropertyRatePassportOffer(rate, property) : undefined,
    [property, rate],
  )
  const showInclusions = showNoRateInclusions || showRateInclusions || !!passportOffer
  const headlineCancellationPolicyStart = getHeadlineCancellationPolicyStart(rate)
  const cancellationPolicy = rate && propertyTimezone ? (
    <PropertyCancellationPolicy
      propertyTimeZone={propertyTimezone}
      headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
      headlineCancellationPolicyStart={headlineCancellationPolicyStart}
      fullyRefundableUntil={rate.fullyRefundableUntil}
      cancellationPolicies={rate.cancellationPolicies}
      currency={rate.currency}
      shouldClarifyRefundIsHotelOnly={rate.includesFlights}
    />
  ) :
    undefined

  const onSelectRate = useCallback(() => {
    onSelect(rate?.optionId)
  }, [onSelect, rate])

  return (
    <AccommodationIndividualSelectRoomOptionTile isSelected={isSelected}>
      <AccommodationIndividualSelectRoomOptionTileDetails
        variant={optionVariant}
        rateName={optionLabel}
        tagLineLabel={<>
          {/* NB: Accommodation service synthesises a `promotion` for tactical rates. */}
          {!!rate?.promotion && <PropertyPromotionLabel promotion={rate.promotion} />}
          {!rate?.promotion && !!rate?.hasAppliedDiscount && <ProductTypeLabel productType="limited_time_special" />}
        </>}
        cancellationPolicy={cancellationPolicy}
      >
        {rate?.promotion && <PropertyPromotionDetails promotion={rate.promotion} />}
        {showInclusions && <PropertyRateInclusions
          property={property}
          inclusions={noRateMode ? roomInclusions : rate.inclusions}
          bonusInclusions={noRateMode ? roomBonusInclusions : rate.bonusInclusions}
          luxPlusInclusions={noRateMode ? roomLuxPlusInclusions : rate.luxPlusInclusions}
          bundleInclusions={noRateMode ? roomBundleInclusions : rate.bundleInclusions}
          passportOffer={passportOffer}
          maxHeight={208}
        />}
      </AccommodationIndividualSelectRoomOptionTileDetails>
      <AccommodationIndividualSelectRoomOptionTilePricing>
        {!noRateMode && rate && <PropertyRatePrice rate={rate} occupancyRoom={occupancyRoom} />}
      </AccommodationIndividualSelectRoomOptionTilePricing>
      <AccommodationIndividualSelectRoomOptionTileCTA>
        {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>
          {intl.formatMessage({
            id: '1IRx8lbZVkU7PYtgPvtT',
            defaultMessage: 'Selected',
            description: 'Button label indicating this room option is currently selected',
          })}
        </TextButton>}
        {!isSelected && <TextButton kind="secondary" onClick={onSelectRate}>
          {intl.formatMessage({
            id: 'p_umY21zQsl1hqz1rtlE',
            defaultMessage: 'Select',
            description: 'Button label to select this room option on the hotel or room booking page',
          })}
        </TextButton>}
      </AccommodationIndividualSelectRoomOptionTileCTA>
    </AccommodationIndividualSelectRoomOptionTile>
  )
}

export default withOfferPageState((state) => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
}), PropertyIndividualRoomSelectRoomOption)
